import React from 'react';
import { FmAssetImage } from './FmAssetImage';
import { FmAssetVideo } from './FmAssetVideo';
import { FmSplit, type FmSplitBalance, type FmSplitSide } from './FmLayout';
import { FmValueList } from './FmValueList';
import { FmBanner } from './FmBanner';
import { ProductAction } from './ProductAction';
import { FmEvidence } from './FmEvidence';
import { FmMapViewportProvider } from './FmMapViewportState';
import { FmSection } from './sections/FmSection';
import type { FmSection as FmSectionDescriptor } from './sections/section.types';
import type { FmElement } from './elements/elements.types';
import type {
  FmProductCatalogDecisionCtaSection,
  FmProductContextualActionsSection,
  FmProductRegisteredMediaSection,
  FmProductSectionDescriptor,
  FmProductSharedSection,
} from './FmProductPage.types';
import { SelectedStandProvider } from '@/contexts/SelectedStandContext';
import { resolveContextualForestMedia } from '@/shared/config/contextualForestMedia';

type Available<T> = Extract<T, { answer: string }>;
type ProductOwnedSection = Exclude<FmProductSectionDescriptor, FmProductSharedSection>;

/** Every element a ready section places, across its slots and its tab or carousel panels. */
function sectionElements(section: FmSectionDescriptor): readonly FmElement[] {
  if (!('slots' in section) || !section.slots) return [];
  const slotted = Object.values(section.slots).flat() as FmElement[];
  const panels =
    section.arrangement === 'tabbed'
      ? section.panels
      : section.arrangement === 'carousel'
        ? section.slides
        : undefined;
  return [...slotted, ...(panels ?? []).flatMap((panel) => panel.elements as readonly FmElement[])];
}

function hostsLiveMap(section: FmSectionDescriptor): boolean {
  return sectionElements(section).some(
    (element) => element.element === 'map' && element.figure?.kind === 'live',
  );
}

/**
 * A shared section inside the product chapter. Live maps read the selected
 * stand and viewport from the providers the product page owns.
 */
const SharedSection: React.FC<{ section: FmProductSharedSection; dominant: boolean }> = ({
  section,
  dominant,
}) => (
  <div
    id={section.id}
    className="fm-product-section"
    data-capability={section.capability}
    data-role={section.role}
    data-dominant={dominant ? 'true' : 'false'}
    data-product-live-map={hostsLiveMap(section.section) ? 'true' : undefined}
    data-product-carousel={section.section.arrangement === 'carousel' ? 'true' : undefined}
    data-testid={`product-section-${section.id}`}
  >
    <SelectedStandProvider>
      <FmMapViewportProvider>
        <FmSection section={section.section} presentation="embedded" headingLevel={3} />
      </FmMapViewportProvider>
    </SelectedStandProvider>
  </div>
);

/**
 * Media presentation to split recipe: the proof takes the media-dominant
 * track; balanced media sits on the balanced recipe, mirrored when the media
 * leads. Copy always reads first once stacked.
 */
const MEDIA_SPLIT: Record<
  NonNullable<Available<FmProductRegisteredMediaSection>['presentation']>,
  { balance: FmSplitBalance; side: FmSplitSide; align: 'start' | 'center' }
> = {
  balanced: { balance: 'balanced', side: 'end', align: 'center' },
  'balanced-media-first': { balance: 'balanced', side: 'start', align: 'center' },
  'proof-dominant': { balance: 'media-dominant', side: 'end', align: 'start' },
};

const RegisteredMedia: React.FC<{
  section: Available<FmProductRegisteredMediaSection>;
}> = ({ section }) => {
  if (section.mediaKind === 'video') {
    return <FmAssetVideo section={section} />;
  }
  const contextMediaIdentity = section.contextMedia
    ? resolveContextualForestMedia(section.contextMedia)
    : null;
  const split = MEDIA_SPLIT[section.presentation ?? 'balanced'];
  return (
    <div
      className="fm-product-registered-media"
      data-context-media={contextMediaIdentity ? 'true' : 'false'}
      data-presentation={section.presentation ?? 'balanced'}
      data-mobile-media={section.mobileMediaIdentity ? 'true' : 'false'}
    >
      <FmSplit
        balance={split.balance}
        side={split.side}
        align={split.align}
        stack={section.role === 'detail'}
        contentSurface="plain"
        content={
          <div
            className={
              /* design-rescue-ok: DS-owned media copy column. */ 'fm-product-registered-media__copy'
            }
          >
            <h3 className="fm-product-answer">{section.answer}</h3>
            {section.explanation ? (
              <p className="fm-product-explanation">{section.explanation}</p>
            ) : null}
            <FmValueList
              density="compact"
              items={section.facts.map((fact) => ({
                id: fact.id,
                label: fact.label,
                value: `${fact.value}${fact.unit ? ` ${fact.unit}` : ''}`,
              }))}
            />
          </div>
        }
        graphic={
          <div className="fm-product-registered-media__visuals">
            {contextMediaIdentity && section.contextCaption ? (
              <figure className="fm-product-registered-media__figure fm-product-registered-media__figure--context">
                <FmAssetImage
                  assetKey={contextMediaIdentity}
                  cropRole="split"
                  data-testid={`product-context-media-${section.id}`}
                />
                <figcaption>{section.contextCaption}</figcaption>
              </figure>
            ) : null}
            <figure
              className={
                /* design-rescue-ok: DS-owned proof media figure. */ 'fm-product-registered-media__figure fm-product-registered-media__figure--proof'
              }
            >
              <FmAssetImage
                assetKey={section.mediaIdentity}
                cropRole="split"
                className="fm-product-registered-media__asset fm-product-registered-media__asset--desktop"
                data-testid={`product-media-${section.id}`}
              />
              {section.mobileMediaIdentity ? (
                <FmAssetImage
                  assetKey={section.mobileMediaIdentity}
                  cropRole="card"
                  className="fm-product-registered-media__asset fm-product-registered-media__asset--mobile"
                  data-testid={`product-mobile-media-${section.id}`}
                />
              ) : null}
              <figcaption>{section.caption}</figcaption>
            </figure>
          </div>
        }
      />
    </div>
  );
};

const StateNotice: React.FC<{ state: ProductOwnedSection['state'] }> = ({ state }) => {
  if (state.status === 'ready') return null;
  const action =
    state.status === 'locked' ? state.action : state.status === 'error' ? state.retry : undefined;
  return (
    <FmBanner
      status={state.status}
      title={state.label}
      description={'detail' in state && state.detail ? state.detail : undefined}
      timestamp={'updatedAt' in state ? state.updatedAt : undefined}
      action={action ? <ProductAction action={action} /> : undefined}
      data-testid={`product-state-${state.status}`}
    />
  );
};

const SectionShell: React.FC<{
  section: ProductOwnedSection;
  dominant: boolean;
  children?: React.ReactNode;
}> = ({ section, dominant, children }) => {
  const catalog =
    section.capability === 'catalog-decision-cta' && 'catalog' in section ? section.catalog : null;
  return (
    <section
      id={section.id}
      className="fm-product-section"
      data-testid={`product-section-${section.id}`}
      data-capability={section.capability}
      data-role={section.role}
      data-dominant={dominant ? 'true' : 'false'}
      data-catalog-prominence={catalog?.prominence}
      data-catalog-audience={catalog?.audience}
    >
      <div className="fm-product-section__body">
        <StateNotice state={section.state} />
        {children}
        {section.evidence ? (
          <FmEvidence evidence={section.evidence} data-testid={`product-evidence-${section.id}`} />
        ) : null}
      </div>
    </section>
  );
};

const ContextualActions: React.FC<{
  section: Available<FmProductContextualActionsSection>;
}> = ({ section }) => (
  <div className="fm-product-actions">
    <h3 className="fm-product-answer">{section.answer}</h3>
    <p className="fm-product-explanation">{section.context}</p>
    <div className="fm-product-actions__rail">
      {section.actions.map((action) => (
        <ProductAction key={action.id} action={action} />
      ))}
    </div>
  </div>
);

const CatalogDecision: React.FC<{
  section: Available<FmProductCatalogDecisionCtaSection>;
}> = ({ section }) => (
  <div className="fm-product-catalog-decision">
    <div>
      <h3 className="fm-product-catalog-decision__offer">{section.catalog.offerLabel}</h3>
      <p
        className="fm-product-catalog-decision__price"
        data-price-status={section.catalog.priceStatus}
      >
        {section.catalog.priceLabel}
      </p>
      {section.catalog.billingLabel !== section.catalog.offerLabel && (
        <p>{section.catalog.billingLabel}</p>
      )}
      <p className="fm-product-answer">{section.answer}</p>
      {!section.catalog.audience && <p>{section.catalog.entitlementLabel}</p>}
    </div>
    <div>
      {section.catalog.inheritanceLabel && (
        <p className="fm-product-catalog-decision__inheritance">
          {section.catalog.inheritanceLabel}
        </p>
      )}
      <ul>
        {section.catalog.includes.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {section.catalog.scopeLabel && (
        <p className="fm-product-catalog-decision__scope">{section.catalog.scopeLabel}</p>
      )}
      <div className="fm-product-actions__rail">
        <ProductAction action={section.primaryAction} />
        {section.secondaryAction ? <ProductAction action={section.secondaryAction} /> : null}
      </div>
      {section.catalog.catalogVersion ? (
        <small data-catalog-record={section.catalog.recordId}>
          {section.catalog.catalogVersion}
        </small>
      ) : null}
    </div>
    {section.catalog.relationship ? (
      <div
        className="fm-product-catalog-decision__relationship"
        role="note"
        aria-label={`${section.catalog.offerLabel} ${section.catalog.relationship.label} ${section.catalog.relationship.targetLabel}`}
        data-testid={`product-catalog-relationship-${section.catalog.recordId}`}
        data-target-catalog-record={section.catalog.relationship.targetRecordId}
      >
        <strong>{section.catalog.offerLabel}</strong>
        <span aria-hidden="true">→</span>
        <span>{section.catalog.relationship.label}</span>
        <span aria-hidden="true">→</span>
        <strong>{section.catalog.relationship.targetLabel}</strong>
      </div>
    ) : null}
  </div>
);

export const ProductSectionRenderer: React.FC<{
  section: FmProductSectionDescriptor;
  dominant: boolean;
}> = ({ section, dominant }) => {
  if (section.capability === 'section') {
    return <SharedSection section={section} dominant={dominant} />;
  }
  if (!['ready', 'partial', 'stale'].includes(section.state.status)) {
    return <SectionShell section={section} dominant={dominant} />;
  }
  switch (section.capability) {
    case 'registered-media':
      return (
        <SectionShell section={section} dominant={dominant}>
          <RegisteredMedia section={section as Available<FmProductRegisteredMediaSection>} />
        </SectionShell>
      );
    case 'contextual-actions':
      return (
        <SectionShell section={section} dominant={dominant}>
          <ContextualActions section={section as Available<FmProductContextualActionsSection>} />
        </SectionShell>
      );
    case 'catalog-decision-cta':
      return (
        <SectionShell section={section} dominant={dominant}>
          <CatalogDecision section={section as Available<FmProductCatalogDecisionCtaSection>} />
        </SectionShell>
      );
  }
};
